import { useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { User } from '#types/System.types';
import { createDocumentStore, type DocumentStore } from '../store';

interface UseUserProps {
    userId: string | undefined;
    enabled?: boolean;
}

export type UseUserReturn = {
    data: User | null;
    loading: boolean;
};

interface UseUserHookReturn {
    user: UseUserReturn;
}

const inactiveStore = createDocumentStore<User>('users/__inactive__');

/**
 * Hook to fetch and bind to a user document.
 */
export const useUser = ({
    userId,
    enabled = !!userId,
}: UseUserProps): UseUserHookReturn => {
    const userStore = useMemo<DocumentStore<User>>(
        () => userId ? createDocumentStore<User>(`users/${userId}`) : inactiveStore,
        [userId],
    );

    const isActive = enabled && !!userId;

    useEffect(() => {
        if (!isActive) return;
        userStore.connect();
        return () => userStore.disconnect();
    }, [isActive, userStore]);

    const snap = useSnapshot(userStore.state);

    const user: UseUserReturn = {
        data: isActive ? (snap.data as User | null) : null,
        loading: isActive ? snap.loading : false,
    };

    return { user };
};
